import { motion } from "framer-motion";
import { Footprints, Sailboat, MapPin, Navigation } from "lucide-react";
import { MAPS_EMBED, MAPS_DIRECTIONS } from "@/lib/site";
import { useLanguage } from "@/lib/i18n";
import WeatherWidget from "@/components/WeatherWidget";

const EASE = [0.22, 1, 0.36, 1];

const OPTION_ICONS = [Footprints, Sailboat];

export default function HowToReachSection() {
  const { t } = useLanguage();

  return (
    <section
      id="como-chegar"
      data-testid="location-section"
      className="mx-auto max-w-7xl px-5 py-24 sm:px-8 sm:py-32 lg:px-10"
    >
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-80px" }}
        transition={{ duration: 0.9, ease: EASE }}
        className="mb-10 max-w-2xl"
      >
        <p className="font-jetmono text-xs uppercase tracking-[0.25em] text-[#0D5C63]" data-testid="location-eyebrow">
          {t.reach.eyebrow}
        </p>
        <h2 className="mt-4 font-playfair text-3xl leading-tight tracking-tight text-[#1A252C] sm:text-4xl lg:text-5xl">
          {t.reach.titleA}{" "}
          <span className="italic text-[#E76F51]">{t.reach.titleB}</span>
        </h2>
        <p className="mt-4 text-base leading-relaxed text-[#1A252C]/60 sm:text-lg">
          {t.reach.subtitle}
        </p>
      </motion.div>

      <WeatherWidget />

      <div className="grid gap-6 lg:grid-cols-2">
        {t.reach.options.map((option, i) => {
          const Icon = OPTION_ICONS[i];
          return (
            <motion.article
              key={i}
              data-testid={`route-${i === 0 ? "trilha" : "barco"}`}
              initial={{ opacity: 0, y: 36 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 0.9, ease: EASE, delay: i * 0.12 }}
              className="group rounded-[2rem] border border-[#3D2B1F]/10 bg-[#FCFBF7] p-7 shadow-lg shadow-[#3D2B1F]/5 transition-all duration-500 hover:-translate-y-1.5 hover:shadow-2xl hover:shadow-[#3D2B1F]/10 sm:p-9"
            >
              <div className="flex items-start justify-between gap-4">
                <div className="grid place-items-center rounded-2xl bg-[#0D5C63]/10 p-3 text-[#0D5C63] transition-colors duration-500 group-hover:bg-[#E76F51] group-hover:text-[#FAF6EE]">
                  <Icon size={24} />
                </div>
                <div className="text-right font-jetmono text-[10px] uppercase tracking-[0.18em] text-[#1A252C]/50">
                  <div>{option.time}</div>
                  <div className="mt-1 text-[#2A9D8F]">{option.level}</div>
                </div>
              </div>
              <h3 className="mt-5 font-cabinet text-xl font-bold tracking-tight text-[#1A252C] sm:text-2xl">
                {option.title}
              </h3>
              <ol className="mt-5 space-y-3.5">
                {option.steps.map((step, j) => (
                  <li key={j} className="flex gap-3.5 text-sm leading-relaxed text-[#1A252C]/65">
                    <span className="mt-0.5 grid h-6 w-6 shrink-0 place-items-center rounded-full bg-[#E9C46A]/30 font-jetmono text-[10px] text-[#3D2B1F]">
                      {j + 1}
                    </span>
                    {step}
                  </li>
                ))}
              </ol>
            </motion.article>
          );
        })}
      </div>

      <motion.div
        initial={{ opacity: 0, y: 36 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-80px" }}
        transition={{ duration: 0.9, ease: EASE }}
        className="mt-8 grid gap-6 lg:grid-cols-5"
      >
        <div className="flex flex-col justify-center rounded-[2rem] border border-[#3D2B1F]/10 bg-[#F4EBD9] p-7 sm:p-9 lg:col-span-2">
          <div>
            <div className="flex items-center gap-2 font-jetmono text-[11px] uppercase tracking-[0.18em] text-[#0D5C63]">
              <MapPin size={13} />
              {t.reach.coordsLabel}
            </div>
            <p className="mt-2 font-jetmono text-sm text-[#1A252C]/70" data-testid="location-coordinates">
              23°31'S · 45°06'W — Praia das Sete Fontes, Ubatuba/SP
            </p>
            <a
              data-testid="location-directions-button"
              href={MAPS_DIRECTIONS}
              target="_blank"
              rel="noopener noreferrer"
              className="mt-5 inline-flex items-center gap-2.5 rounded-full bg-[#0D5C63] px-6 py-3 font-cabinet text-sm font-bold text-[#FAF6EE] transition-all duration-300 hover:-translate-y-0.5 hover:bg-[#082F38]"
            >
              <Navigation size={15} />
              {t.reach.directions}
            </a>
          </div>
        </div>

        <div className="overflow-hidden rounded-[2rem] border border-[#3D2B1F]/10 shadow-lg shadow-[#3D2B1F]/5 lg:col-span-3" data-testid="location-map-card">
          <iframe
            data-testid="location-map-iframe"
            title={t.reach.mapTitle}
            src={MAPS_EMBED}
            className="h-[340px] w-full sm:h-full sm:min-h-[420px]"
            loading="lazy"
            referrerPolicy="no-referrer-when-downgrade"
            allowFullScreen
          />
        </div>
      </motion.div>
    </section>
  );
}
